import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ArrowLeft, ArrowUpRight, Clock } from 'lucide-react';
import { GUIDES } from '../data/contentData';
import { useGuide } from '../data/hooks';
import { getSupplierBySlug } from '../data/mockData';
import SEO, { siteUrl } from '../components/SEO';
import Breadcrumbs from '../components/Breadcrumbs';
import GuideBody, { extractFaqs } from '../components/GuideBody';
import { LoadingState, ErrorState } from '../components/states/AsyncStates';
import { track, EVENTS } from '../lib/analytics';
import Img from '../components/Img';

const estimateWordCount = (body) => {
  if (!body) return 0;
  let words = 0;
  if (body.executiveSummary) words += body.executiveSummary.split(/\s+/).length;
  for (const b of body.sections || []) {
    if (b.text) words += b.text.split(/\s+/).length;
    if (b.items) for (const it of b.items) words += String(it).split(/\s+/).length;
    if (b.rows) for (const r of b.rows) for (const c of r) words += String(c).split(/\s+/).length;
    if (b.body) words += b.body.split(/\s+/).length;
  }
  return words;
};

const GuideDetail = () => {
  const { slug } = useParams();
  const { data: guide, loading, error, refetch } = useGuide(slug);

  // Hooks must run unconditionally before any early return.
  useEffect(() => {
    if (!guide) return;
    track(EVENTS.GUIDE_VIEWED, {
      slug: guide.slug,
      category: guide.category,
      word_count: estimateWordCount(guide.body),
    });
  }, [guide?.slug]); // eslint-disable-line react-hooks/exhaustive-deps

  if (loading) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><LoadingState label="Loading guide…" /></div>;
  }
  if (error) {
    return <div className="mx-auto max-w-2xl px-5 py-24"><ErrorState description={error?.message} onRetry={refetch} /></div>;
  }
  if (!guide) {
    return (
      <div className="mx-auto max-w-2xl px-5 py-24 text-center" data-testid="guide-not-found">
        <div className="label-overline">404</div>
        <h1 className="mt-4 font-display text-4xl tracking-tighter">Guide not found</h1>
        <Link to="/sourcing-guides" className="mt-6 inline-block btn-giwahs btn-giwahs-primary">Back to guides</Link>
      </div>
    );
  }

  const relatedSupplier = guide.related?.supplierSlug ? getSupplierBySlug(guide.related.supplierSlug) : null;
  const relatedCategory = guide.related?.categorySlug;
  const more = GUIDES.filter(g => g.slug !== guide.slug).slice(0, 3);
  const body = guide.body;
  const faqs = extractFaqs(body);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: guide.title,
    description: guide.summary,
    image: guide.cover,
    author: { '@type': 'Organization', name: guide.author || 'GIWAHS Editorial' },
    publisher: { '@type': 'Organization', name: 'GIWAHS', logo: { '@type': 'ImageObject', url: `${siteUrl}/logo.png` } },
    datePublished: '2026-02-27',
    dateModified: '2026-02-27',
    mainEntityOfPage: { '@type': 'WebPage', '@id': `${siteUrl}/sourcing-guides/${guide.slug}` },
    articleSection: guide.categoryLabel,
    wordCount: body ? estimateWordCount(body) : undefined,
  };

  const jsonLd = faqs.length > 0
    ? [articleJsonLd, {
        '@context': 'https://schema.org',
        '@type': 'FAQPage',
        mainEntity: faqs.map(f => ({
          '@type': 'Question',
          name: f.question,
          acceptedAnswer: { '@type': 'Answer', text: f.answer },
        })),
      }]
    : articleJsonLd;

  const breadcrumbItems = [
    { name: 'Home', url: '/' },
    { name: 'Sourcing Guides', url: '/sourcing-guides' },
    { name: guide.title, url: `/sourcing-guides/${guide.slug}` },
  ];

  return (
    <div data-testid="guide-detail-page" className="surface-obsidian text-platinum">
      <SEO
        title={guide.title}
        description={guide.summary}
        image={guide.cover}
        type="article"
        breadcrumbs={breadcrumbItems}
        jsonLd={jsonLd}
      />
      {/* Breadcrumbs */}
      <div className="border-b border-hairline bg-[#0A0A0A]">
        <div className="mx-auto max-w-[1440px] px-5 py-3 md:px-8">
          <Breadcrumbs items={breadcrumbItems} />
        </div>
      </div>
      {/* HERO */}
      <section className="border-b border-hairline surface-obsidian">
        <div className="mx-auto max-w-[1100px] px-5 py-12 md:px-8 md:py-20">
          <Link to="/sourcing-guides" className="inline-flex items-center gap-2 label-overline text-platinum-2 hover:text-gold" data-testid="guide-back">
            <ArrowLeft size={14} strokeWidth={1.5} /> All guides
          </Link>
          <div className="mt-8 flex items-center gap-3">
            <span className="label-overline giwahs-chip-dark">{guide.categoryLabel}</span>
            <span className="flex items-center gap-1.5 font-mono-data text-xs text-neutral-600"><Clock size={12} strokeWidth={1.5} /> {guide.readingMin} min read</span>
            <span className="font-mono-data text-xs text-neutral-500">{guide.author}</span>
          </div>
          <h1 className="mt-6 font-display tracking-tighter" style={{ fontWeight: 700, fontSize: 'clamp(2.5rem, 6vw, 5rem)', lineHeight: 0.92 }}>
            {guide.title}
          </h1>
          <p className="mt-6 text-lg leading-relaxed text-neutral-700">{guide.summary}</p>
        </div>
        <div className="mx-auto max-w-[1440px] px-5 md:px-8">
          <div className="aspect-[21/9] w-full overflow-hidden border-x border-t border-black">
            <Img src={guide.cover} alt={`${guide.title} — editorial sourcing guide cover`} variant="heroNarrow" priority className="h-full w-full object-cover" />
          </div>
        </div>
      </section>

      {/* ARTICLE BODY */}
      <section className="surface-obsidian text-platinum">
        <div className="mx-auto max-w-[760px] px-5 py-14 md:px-8 md:py-20">
          {body ? (
            <GuideBody body={body} guideSlug={guide.slug} />
          ) : (
            <div className="border border-dashed border-black bg-[#F5F5F0] p-8 md:p-12">
              <div className="label-overline">Editorial preview</div>
              <h2 className="mt-3 font-display text-2xl tracking-tighter md:text-3xl" style={{ fontWeight: 600 }}>
                Editorial preview in progress.
              </h2>
              <p className="mt-4 text-sm leading-relaxed text-neutral-700">
                This guide is being authored by the GIWAHS editorial desk and will publish shortly.
              </p>
            </div>
          )}
        </div>
      </section>

      {/* RELATED LINKS */}
      <section className="border-t border-neutral-200 bg-white">
        <div className="mx-auto max-w-[1100px] px-5 py-12 md:px-8 md:py-16">
          <span className="label-overline">/ Continue your sourcing research</span>
          <div className="mt-6 grid gap-4 md:grid-cols-3">
            {relatedSupplier && (
              <Link to={`/supplier/${relatedSupplier.slug}`} data-testid="guide-related-supplier" className="group border border-black p-5 hover:bg-neutral-50">
                <span className="label-overline text-neutral-500">Featured Supplier</span>
                <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{relatedSupplier.name}</div>
                <div className="mt-3 inline-flex items-center gap-1 label-overline">View profile <ArrowUpRight size={12} strokeWidth={1.5} /></div>
              </Link>
            )}
            {relatedCategory && (
              <Link to={`/category/${relatedCategory}`} data-testid="guide-related-category" className="group border border-black p-5 hover:bg-neutral-50">
                <span className="label-overline text-neutral-500">Related Directory</span>
                <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{relatedCategory.split('-').map(w => w[0].toUpperCase() + w.slice(1)).join(' ')}</div>
                <div className="mt-3 inline-flex items-center gap-1 label-overline">Browse factories <ArrowUpRight size={12} strokeWidth={1.5} /></div>
              </Link>
            )}
            {guide.related.country && (
              <Link to={`/manufacturers/${guide.related.country.toLowerCase()}`} data-testid="guide-related-country" className="group border border-black p-5 hover:bg-neutral-50">
                <span className="label-overline text-neutral-500">Country Sourcing</span>
                <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>Manufacturers in {guide.related.country}</div>
                <div className="mt-3 inline-flex items-center gap-1 label-overline">Country intelligence brief <ArrowUpRight size={12} strokeWidth={1.5} /></div>
              </Link>
            )}
          </div>
        </div>
      </section>

      {/* MORE GUIDES */}
      <section className="border-t border-black bg-[#F5F5F0]">
        <div className="mx-auto max-w-[1440px] px-5 py-14 md:px-8 md:py-20">
          <span className="label-overline">/ More guides</span>
          <div className="mt-6 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
            {more.map(m => (
              <Link key={m.slug} to={`/sourcing-guides/${m.slug}`} className="group border border-black bg-white">
                <div className="aspect-[16/10] overflow-hidden">
                  <Img src={m.cover} alt={`${m.title} — related sourcing guide`} variant="card" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                </div>
                <div className="p-5">
                  <span className="label-overline">{m.categoryLabel}</span>
                  <div className="mt-3 font-display text-lg tracking-tight" style={{ fontWeight: 600 }}>{m.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default GuideDetail;
